import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { forkJoin } from 'rxjs';
import { RentalService } from '../../../core/api/rental.service';
import { ExtraServiceService } from '../../../core/api/extra-service.service';
import { Image } from '../../../core/models/image.model';
import { RentalExtraService, RentalReturn } from '../../../core/models/rental.model';
import { ExtraService } from '../../../core/models/extra-service.model';
import { parseApiDateTime, toApiDateTime } from '../../../shared/utils/date.utils';
import { DatetimeInputComponent } from '../../../shared/components/datetime-input/datetime-input.component';
import { ImageUploadComponent } from '../../../shared/components/image-upload/image-upload.component';

@Component({
  selector: 'app-rental-return',
  standalone: true,
  imports: [
    CommonModule, RouterModule, ReactiveFormsModule, MatCardModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatCheckboxModule, MatDividerModule,
    MatSnackBarModule, MatProgressSpinnerModule,
    DatetimeInputComponent, ImageUploadComponent,
  ],
  templateUrl: './rental-return.component.html',
  styleUrl: './rental-return.component.scss',
})
export class RentalReturnComponent implements OnInit {
  form: FormGroup;
  submitting = false;
  rentalId!: number;
  /** Список всех активных услуг, который рисуем чекбоксами. */
  extraServices: ExtraService[] = [];

  private readonly apiFieldToControl: Record<string, string> = {
    mileage_after: 'mileageAfter',
    fuel_level_after: 'fuelLevelAfter',
    return_location: 'returnLocation',
    return_datetime: 'returnDate',
    images_after: 'imagesAfter',
    // FormArray — `setServerErrors` для него не сработает корректно,
    // но маппинг держим для единообразия; реальные ошибки по этому
    // полю бэк присылает без привязки к контролу — показываем в snackBar.
    extra_services: 'extraServices',
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private rentalService: RentalService,
    private extraServiceService: ExtraServiceService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      mileageAfter: [null, [Validators.required, Validators.min(0)]],
      fuelLevelAfter: [null, Validators.min(1)],
      returnLocation: ['', Validators.required],
      returnDate: [new Date() as Date | null, Validators.required],
      imagesAfter: [[] as Image[]],
      // FormArray под доп. услуги. На каждую услугу — FormGroup
      // (`service` id disabled, `selected` bool, `amount` number|null).
      extraServices: this.fb.array([] as FormGroup[]),
    });
  }

  get extraServicesForm(): FormArray<FormGroup> {
    return this.form.get('extraServices') as FormArray<FormGroup>;
  }

  ngOnInit(): void {
    this.rentalId = Number(this.route.snapshot.paramMap.get('id'));
    if (!this.rentalId) {
      this.router.navigate(['/rentals']);
      return;
    }
    // Загружаем параллельно прокат (для предзаполнения уже привязанных
    // доп. услуг) и список активных услуг. Основные поля формы
    // (mileage/location/date) НЕ патчим — user вводит их вручную при приёме.
    forkJoin({
      rental: this.rentalService.get(this.rentalId),
      services: this.extraServiceService.list(true),
    }).subscribe({
      next: ({ rental, services }) => {
        this.extraServices = services;
        this.buildExtraServicesForm(services, rental.extra_services ?? []);
      },
      error: () => {
        // Если что-то не загрузилось — форма останется без чекбоксов услуг,
        // пользователь сможет принять авто как и раньше. Не блокируем flow.
        this.extraServices = [];
      },
    });
  }

  /**
   * Строит FormArray под список активных услуг, предзаполняя
   * `selected`/`amount` по уже привязанным к прокату услугам
   * (матч по `service` id).
   */
  private buildExtraServicesForm(services: ExtraService[], bound: RentalExtraService[]): void {
    while (this.extraServicesForm.length > 0) {
      this.extraServicesForm.removeAt(0);
    }
    for (const es of services) {
      const linked = bound.find((b) => b.service === es.id);
      const selected = !!linked;
      const amount = linked ? Number(linked.amount) : null;
      const group = this.fb.group({
        service: [{ value: es.id, disabled: true }, Validators.required],
        selected: [selected],
        amount: this.fb.control<number | null>(Number.isFinite(amount) ? amount : null, [Validators.min(0.01)]),
      });
      this.extraServicesForm.push(group);
    }
  }

  /**
   * Сбор `extra_services` из FormArray. В режиме приёма ВСЕГДА отправляем
   * полный набор (включая пустой `[]`) — у бэка replace-режим, поэтому
   * отсутствие поля оставит старый набор нетронутым.
   */
  private collectExtraServices(): { service: number; amount: number }[] {
    return this.extraServicesForm.controls
      .map((group) => group.getRawValue() as { service: number; selected: boolean; amount: number | null })
      .filter((row) => row.selected && row.amount != null && Number(row.amount) > 0)
      .map((row) => ({ service: row.service, amount: Number(row.amount) }));
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) return;
    this.submitting = true;
    const v = this.form.value;
    const returnDate = v.returnDate ? parseApiDateTime(v.returnDate) : null;
    if (!returnDate) {
      this.form.markAllAsTouched();
      this.submitting = false;
      return;
    }
    const data: RentalReturn = {
      mileage_after: v.mileageAfter,
      return_location: v.returnLocation,
      return_datetime: toApiDateTime(returnDate),
      fuel_level_after: v.fuelLevelAfter,
      images_after: (v.imagesAfter || []).map((img: Image) => img.id),
      // replace-режим: даже пустой массив отправляем, чтобы снять все услуги.
      extra_services: this.collectExtraServices(),
    };
    this.rentalService.returnCar(this.rentalId, data).subscribe({
      next: () => {
        this.snackBar.open('Автомобиль принят', 'ОК', { duration: 3000 });
        this.router.navigate(['/rentals', this.rentalId]);
      },
      error: (err: any) => {
        this.submitting = false;
        const errorData = err.error;
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          this.setServerErrors(errorData);
          // Вложенные ошибки (`extra_services`) бэк может прислать без
          // маппинга на конкретный контрол — показываем в snackBar.
          const nonField = errorData.all || errorData.non_field_errors || errorData.extra_services;
          if (nonField) {
            const msg = Array.isArray(nonField) ? nonField[0] : String(nonField);
            this.snackBar.open(msg, 'ОК', { duration: 5000 });
          }
        } else {
          const msg = Array.isArray(errorData) ? errorData[0] : err.error?.all?.[0] || 'Ошибка приёма';
          this.snackBar.open(msg, 'ОК', { duration: 5000 });
        }
      },
    });
  }

  private setServerErrors(errors: Record<string, any>): void {
    for (const [apiField, controlName] of Object.entries(this.apiFieldToControl)) {
      const control = this.form.get(controlName);
      if (control && errors.hasOwnProperty(apiField)) {
        const messages = errors[apiField];
        const msg = Array.isArray(messages) ? messages[0] : String(messages);
        control.setErrors({ ...control.errors, serverError: msg });
      }
    }
  }
}
